import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Info, Wallet, Users } from "lucide-react";
import PublicLayout, { PageHero } from "../../components/PublicLayout";
import { useSEO, buildFaqJsonLd } from "../../lib/seo";
import api from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { PRICING_CONTENT } from "../../data/pricingContent";

/**
 * Pricing page.
 *
 * The reason this page exists: visitors always wonder what it costs, and
 * that moment always arrives. A marketplace without a pricing page loses the
 * visitor right there.
 *
 * **All numbers come from /api/pricing.** Hardcoding them here turns the page
 * into a snapshot of what someone thought on a Tuesday. Next time the fee or
 * a credit pack changes, the page silently lies. An incorrect pricing claim
 * on an indexed page is misleading marketing, not a typo. The example
 * numbers are computed by the same module the checkout uses, so what the
 * page shows is what the checkout charges.
 *
 * Two audiences, two very different pricing models — that's why the page has
 * a toggle up top instead of a two-column table. Organisers pay a fee on top
 * of the artist's price. Artists pay credits to unlock a request and
 * commission when the job happens. Mixing them into one table makes both
 * incomprehensible.
 *
 * Copy for both languages lives in `data/pricingContent.js` (SSOT).
 */

const kr = (ore, lang) => {
  const n = Number(ore);
  if (!Number.isFinite(n)) return "";
  const whole = Math.trunc(n / 100);
  const rest = Math.abs(n % 100);
  const s = new Intl.NumberFormat(lang === "sv" ? "sv-SE" : "en-US").format(whole);
  const unit = lang === "sv" ? "kr" : "SEK";
  return rest ? `${s},${String(rest).padStart(2, "0")} ${unit}` : `${s} ${unit}`;
};

const fill = (template, vars) => Object.keys(vars).reduce(
  (acc, k) => acc.replaceAll(`{${k}}`, vars[k]),
  template || "",
);

const Toggle = ({ side, setSide, c }) => (
  <div className="flex items-center gap-4" data-testid="pricing-toggle-wrap">
    <div className="hidden sm:block flex-1 h-px bg-gradient-to-r from-transparent to-[#2A2A2A]" aria-hidden="true" />
    <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[#181818] border border-[#2A2A2A] shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_10px_40px_-20px_rgba(255,92,0,0.35)]" data-testid="pricing-toggle">
      {[["client", c.toggleBook, Users], ["talent", c.toggleArtist, Wallet]].map(([k, l, I]) => (
        <button key={k} type="button" onClick={() => setSide(k)} data-testid={`pricing-side-${k}`}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[14px] font-medium transition-colors ${
            side === k ? "bg-[#1A8A4A] text-white" : "text-neutral-400 hover:text-white"}`}>
          <I size={15} /> {l}
        </button>
      ))}
    </div>
    <div className="hidden sm:block flex-1 h-px bg-gradient-to-l from-transparent to-[#2A2A2A]" aria-hidden="true" />
  </div>
);

const Big = ({ value, label, sub }) => (
  <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-7">
    <div className="text-[38px] font-extrabold tracking-tight text-white leading-none">{value}</div>
    <div className="mt-3 text-[15px] font-semibold text-white">{label}</div>
    {sub && <p className="mt-2 text-[14px] leading-relaxed text-neutral-400">{sub}</p>}
  </div>
);

const ClientSide = ({ d, lang, c }) => {
  const ex = d?.example;
  const fee = d?.fee?.fee_percent ?? 10;
  const vat = d?.fee?.fee_vat_percent ?? 25;
  const cap = d?.caps?.offer_cap ?? 5;
  const zero = kr(0, lang);
  return (
    <div data-testid="pricing-client">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Big value={zero} label={c.big1Label} sub={c.big1Sub} />
        <Big value={zero} label={fill(c.big2Label, { cap })} sub={c.big2Sub} />
        <Big value={`${fee} %`} label={c.big3Label} sub={fill(c.big3Sub, { vat })} />
      </div>

      {ex && (
        <div className="mt-8 rounded-2xl border border-[#2A2A2A] bg-[#181818] overflow-hidden" data-testid="pricing-example">
          <div className="px-7 py-5 border-b border-[#2A2A2A]">
            <h3 className="text-[18px] font-semibold text-white">{c.exampleTitle}</h3>
            <p className="mt-1 text-[14px] text-neutral-400">{c.exampleSub}</p>
          </div>
          <dl className="px-7 py-5 space-y-3 text-[15px]">
            <div className="flex justify-between gap-6">
              <dt className="text-neutral-400">{c.artistPrice}</dt>
              <dd className="text-white tabular-nums">{kr(ex.contract_total_ore, lang)}</dd>
            </div>
            <div className="flex justify-between gap-6">
              <dt className="text-neutral-400">{fill(c.platformFee, { fee })}</dt>
              <dd className="text-white tabular-nums">{kr(ex.platform_fee_ore, lang)}</dd>
            </div>
            <div className="flex justify-between gap-6">
              <dt className="text-neutral-400">{c.vatOnFee}</dt>
              <dd className="text-white tabular-nums">{kr(ex.platform_fee_vat_ore, lang)}</dd>
            </div>
            <div className="flex justify-between gap-6 pt-3 border-t border-[#2A2A2A]">
              <dt className="text-white font-semibold">{c.youPay}</dt>
              <dd className="text-white font-semibold tabular-nums">{kr(ex.amount, lang)}</dd>
            </div>
            <div className="flex justify-between gap-6">
              <dt className="text-[#2fbe6e]">{c.artistGets}</dt>
              <dd className="text-[#2fbe6e] tabular-nums">{kr(ex.contract_total_ore, lang)}</dd>
            </div>
          </dl>
          <p className="px-7 pb-6 text-[13.5px] text-neutral-500">{c.exampleFooter}</p>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-7">
          <h3 className="text-[17px] font-semibold text-white">{c.neverPayTitle}</h3>
          <ul className="mt-4 space-y-2.5">
            {c.neverPayItems.map((x) => (
              <li key={x} className="flex items-start gap-2.5 text-[15px] text-neutral-300">
                <Check size={17} className="text-[#2fbe6e] mt-0.5 shrink-0" /> {x}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-7">
          <h3 className="text-[17px] font-semibold text-white">{c.howPaymentTitle}</h3>
          <p className="mt-3 text-[15px] leading-relaxed text-neutral-400">
            {fill(c.howPaymentBody1, { days: d?.caps?.payout_delay_days ?? 14 })}
          </p>
          <p className="mt-3 text-[14px] leading-relaxed text-neutral-500">{c.howPaymentBody2}</p>
        </div>
      </div>

      <div className="mt-10 text-center">
        <Link to="/boka" data-testid="pricing-cta-client"
          className="inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-7 py-3.5 rounded-xl transition-colors">
          {c.cta} <ArrowRight size={18} />
        </Link>
        <p className="mt-3 text-[13.5px] text-neutral-500">{c.ctaSub}</p>
      </div>
    </div>
  );
};

const TalentSide = ({ d, lang, c }) => {
  // Talent and agency share the same price list, so we always read the talent
  // packs. The account-toggle used to live here but has no purpose now.
  const packs = d?.packs?.talent || [];
  const fee = d?.fee?.fee_percent ?? 10;
  const caps = d?.caps || {};
  const welcome = caps.welcome_credits ?? 3;
  const unlockCost = caps.unlock_cost_credits ?? 1;
  const cap = caps.unlock_cap ?? 5;
  return (
    <div data-testid="pricing-talent">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Big value={fill(c.big1Value, { n: welcome })} label={c.big1Label} sub={c.big1Sub} />
        <Big value={fill(c.big2Value, { n: unlockCost })} label={c.big2Label} sub={fill(c.big2Sub, { cap })} />
        <Big value={`${fee} %`} label={c.big3Label} sub={c.big3Sub} />
      </div>

      {packs.length > 0 && (
        <div className="mt-10 rounded-3xl border border-[#2A2A2A] bg-gradient-to-b from-[#161616] to-[#101010] p-8 md:p-10" data-testid="pricing-packs">
          <div>
            <h3 className="text-[22px] font-bold text-white tracking-tight">{c.packsTitle}</h3>
            <p className="mt-1 text-[14px] text-neutral-400">{c.packsSub}</p>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-[720px] mx-auto">
            {packs.map((p) => (
              <div key={p.id} data-testid={`pricing-pack-${p.id}`}
                className={`relative rounded-2xl border p-7 transition-colors ${p.popular
                  ? "border-[#1A8A4A]/60 bg-[#0F1F14]"
                  : "border-[#2A2A2A] bg-[#181818] hover:border-[#3a3a3a]"}`}>
                {p.popular && (
                  <div className="absolute -top-2.5 left-7 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#1A8A4A] text-white text-[10.5px] font-bold tracking-wide uppercase">
                    {c.mostChosen}
                  </div>
                )}
                <div className="flex items-baseline gap-2">
                  <div className="text-[42px] font-extrabold text-white leading-none tracking-tight tabular-nums">{p.credits}</div>
                  <div className="text-[14px] text-neutral-400">{p.credits === 1 ? c.credit : c.credits}</div>
                </div>
                <div className="mt-6 flex items-baseline justify-between gap-3 pt-5 border-t border-[#2A2A2A]">
                  <div>
                    <div className="text-[22px] font-bold text-white tabular-nums">{kr(p.price_ore, lang)}</div>
                    <div className="text-[12.5px] text-neutral-500 mt-0.5 tabular-nums">
                      {kr(p.per_credit_ore, lang)} {c.perCredit}
                    </div>
                  </div>
                  {p.popular && <Check size={20} className="text-[#2fbe6e] shrink-0" />}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[13px] text-neutral-500 flex items-center justify-center gap-2">
            <Info size={14} className="shrink-0" />
            {c.pricesInclVat}
          </p>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-7">
          <h3 className="text-[17px] font-semibold text-white">{c.neverPayTitle}</h3>
          <ul className="mt-4 space-y-2.5">
            {c.neverPayItems.map((x) => (
              <li key={x} className="flex items-start gap-2.5 text-[15px] text-neutral-300">
                <Check size={17} className="text-[#2fbe6e] mt-0.5 shrink-0" /> {x}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-7">
          <h3 className="text-[17px] font-semibold text-white">{c.whyCreditsTitle}</h3>
          <p className="mt-3 text-[15px] leading-relaxed text-neutral-400">
            {fill(c.whyCreditsBody1, { cap })}
          </p>
          <p className="mt-3 text-[14px] leading-relaxed text-neutral-500">{c.whyCreditsBody2}</p>
        </div>
      </div>

      <div className="mt-10 text-center">
        <Link to="/join" data-testid="pricing-cta-talent"
          className="inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-7 py-3.5 rounded-xl transition-colors">
          {c.cta} <ArrowRight size={18} />
        </Link>
        <p className="mt-3 text-[13.5px] text-neutral-500">{fill(c.ctaSub, { n: welcome })}</p>
      </div>
    </div>
  );
};

const Pricing = () => {
  const { lang } = useLang();
  const [side, setSide] = useState("client");
  const [d, setD] = useState(null);
  const c = PRICING_CONTENT[lang] || PRICING_CONTENT.en;
  // FAQ jsonLd is per-language: rebuild whenever language switches.
  const jsonLd = useMemo(() => buildFaqJsonLd(c.faq), [c.faq]);

  useSEO({
    title: c.seoTitle,
    description: c.seoDescription,
    lang,
    canonical: "/pricing",
    jsonLd,
  });

  useEffect(() => {
    // If the request fails, show the page anyway with fallback numbers.
    // A pricing page that won't render is worse than one missing its table.
    api.get("/pricing").then(({ data }) => setD(data)).catch(() => setD({}));
  }, []);

  return (
    <PublicLayout>
      <PageHero eyebrow={c.heroEyebrow} title={c.heroTitle} subtitle={c.heroSubtitle} />
      <section className="max-w-[1000px] mx-auto px-6 pb-24" data-testid="pricing-page">
        <Toggle side={side} setSide={setSide} c={c} />
        <div className="mt-10">
          {side === "client"
            ? <ClientSide d={d} lang={lang} c={c.client} />
            : <TalentSide d={d} lang={lang} c={c.talent} />}
        </div>

        <div className="mt-16">
          <h2 className="text-[26px] font-bold text-white text-center">{c.faqTitle}</h2>
          <div className="mt-8 divide-y divide-[#1f1f1f] rounded-2xl border border-[#2A2A2A] bg-[#181818]">
            {c.faq.map((f) => (
              <div key={f.q} className="px-7 py-6">
                <h3 className="text-[16px] font-semibold text-white">{f.q}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-neutral-400">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Pricing;
